import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Home, Shield, CheckCircle2, Phone, Mail, Star, Wrench, PaintBucket, TreeDeciduous, Trash2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FloatingCTAs from '../components/FloatingCTAs';
import AddressAutocomplete from '../components/AddressAutocomplete';
import ImageUploader from '../components/ImageUploader';

export default function ResidentialServices() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    service_needed: '',
    message: ''
  });
  const [photos, setPhotos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Before, during, after project images - cycling infinitely
  const heroImages = [
    'https://customer-assets.emergentagent.com/job_eliteroofing/artifacts/a7cyxo0o_poinciana%201.JPG', // Before
    'https://customer-assets.emergentagent.com/job_eliteroofing/artifacts/rykamsag_poinciana%202.JPG', // In progress 1
    'https://customer-assets.emergentagent.com/job_eliteroofing/artifacts/pjv8cll2_poinciana%203.jpg', // In progress 2
    'https://customer-assets.emergentagent.com/job_eliteroofing/artifacts/a6amv3fk_poinciana%204.jpg', // In progress 3
    'https://customer-assets.emergentagent.com/job_eliteroofing/artifacts/nrq41jyk_poinciana%205.jpg'  // After
  ];

  // Auto-cycle through images every 4 seconds
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const fd = new FormData();
      Object.entries(formData).forEach(([k, v]) => {
        if (v !== null && v !== undefined) fd.append(k, String(v));
      });
      (photos || []).slice(0, 5).forEach((file) => fd.append('photos', file));

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/residential-inquiry/submit`, {
        method: 'POST',
        body: fd,
      });

      if (response.ok) {
        toast.success('Thank you! We\'ll contact you within 24 hours to schedule your free inspection.');
        setFormData({ name: '', phone: '', email: '', address: '', city: '', state: '', zip_code: '', service_needed: '', message: '' });
        setPhotos([]);
      } else {
        toast.error('Something went wrong. Please call us directly at (407) 785-0642.');
      }
    } catch (error) {
      toast.error('Failed to submit. Please call us at (407) 785-0642.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center overflow-hidden">
        {/* Background Image Slideshow */}
        <div className="absolute inset-0 z-0">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentImageIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img 
                src={image}
                alt={`Project progress ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
          
          {/* Light overlay for text readability - 15% opacity */}
          <div className="absolute inset-0 bg-white/15"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
            style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}
          >
            <div className="inline-block mb-6">
              <span className="px-4 py-2 bg-[hsl(var(--accent))] text-white text-sm font-bold uppercase tracking-wide rounded-full shadow-lg">
                Protecting Central Florida Homes
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight drop-shadow-lg">
              Protecting Your Home From the Top Down — Trusted Residential Roofing & Exterior Solutions
            </h1>
            <p className="text-xl sm:text-2xl text-white mb-8 leading-relaxed drop-shadow-md">
              Serving homeowners in Central Florida with expert roof repairs, replacements, and exterior services supported by transparent communication, thorough documentation, and hands-on project management.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={scrollToForm}
                className="h-14 px-8 bg-[hsl(var(--accent))] hover:bg-[#a01830] text-white text-lg font-bold shadow-xl"
                data-testid="free-inspection-btn"
              >
                Free Roof Inspection & Quote
              </Button>
              <Button 
                onClick={scrollToForm}
                variant="outline"
                className="h-14 px-8 bg-white hover:bg-slate-50 text-slate-900 text-lg font-semibold border-2"
                data-testid="schedule-service-btn"
              >
                Schedule Service
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who We Help */}
      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Who We Help
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We're here for Florida homeowners at every stage of homeownership, from your first home to preparing for sale.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: <Home className="h-8 w-8" />, title: 'Florida Homeowners', desc: 'Protecting your most valuable investment with reliable roofing and exterior services' },
              { icon: <Home className="h-8 w-8" />, title: 'First-Time Home Buyers', desc: 'Peace of mind inspections and maintenance to start your homeownership journey right' },
              { icon: <Home className="h-8 w-8" />, title: 'Families Preparing to Sell', desc: 'Pre-sale roof inspections and repairs that increase home value and buyer confidence' },
              { icon: <Shield className="h-8 w-8" />, title: 'Storm Damage Recovery', desc: 'Expert wind, hail, and storm damage assessment with insurance claim support' },
              { icon: <Home className="h-8 w-8" />, title: 'Single-Family Homes', desc: 'Complete roofing and exterior solutions for your family home' },
              { icon: <Home className="h-8 w-8" />, title: 'Townhome Residents', desc: 'Coordinated exterior services that respect your community guidelines' }
            ].map((client, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-xl transition-shadow text-center">
                  <CardHeader>
                    <div className="mx-auto mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 text-[hsl(var(--primary))]">
                      {client.icon}
                    </div>
                    <CardTitle className="text-xl mb-2">{client.title}</CardTitle>
                    <CardDescription className="text-slate-600">{client.desc}</CardDescription>
                  </CardHeader>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Residential Roofing Solutions */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Residential Roofing Solutions
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              From minor repairs to complete replacements, we protect your home with quality workmanship and reliable service.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Roof Repair & Maintenance */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card className="h-full">
                <CardHeader>
                  <div className="mb-4">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-50 text-[hsl(var(--primary))]">
                      <Wrench className="h-7 w-7" />
                    </div>
                  </div>
                  <CardTitle className="text-2xl mb-4">Roof Repair & Maintenance</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {[
                      'Leak diagnosis and repair',
                      'Shingle replacement',
                      'Wind or storm damage repair',
                      'Missing or damaged shingles',
                      'Flashing repairs',
                      'Gutter repairs and cleaning',
                      'Preventative maintenance'
                    ].map((service, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{service}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            {/* Roof Replacement */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card className="h-full">
                <CardHeader>
                  <div className="mb-4">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-50 text-[hsl(var(--primary))]">
                      <Home className="h-7 w-7" />
                    </div>
                  </div>
                  <CardTitle className="text-2xl mb-4">Roof Replacement</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {[
                      'Complete roof replacement',
                      'Architectural shingles',
                      'Metal roofing options',
                      'Flat roof solutions',
                      'Energy-efficient materials',
                      'Durable, long-lasting systems',
                      'Warranty-backed installations'
                    ].map((service, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{service}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            {/* Roof Inspections */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card className="h-full">
                <CardHeader>
                  <div className="mb-4">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-50 text-[hsl(var(--primary))]">
                      <Shield className="h-7 w-7" />
                    </div>
                  </div>
                  <CardTitle className="text-2xl mb-4">Roof Inspections</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {[
                      'Pre-sale inspections',
                      'Post-storm assessments',
                      'Insurance claim support',
                      'Annual maintenance checks',
                      'Detailed photo documentation',
                      'Written inspection reports',
                      'Free initial consultations'
                    ].map((service, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{service}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Exterior Home Services */}
      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Exterior Home Services (Beyond Roofing)
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Complete exterior solutions to enhance your home's curb appeal, protection, and value.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: <Home className="h-6 w-6" />, title: 'Stucco & Exterior Wall Repairs', desc: 'Repair cracks, restore finish, and protect your home from moisture damage' },
              { icon: <PaintBucket className="h-6 w-6" />, title: 'Exterior Painting & Refresh', desc: 'Enhance curb appeal and add lasting protection with professional exterior painting' },
              { icon: <Home className="h-6 w-6" />, title: 'Concrete & Paver Walkways/Driveways', desc: 'Durable, attractive concrete work that increases home value and functionality' },
              { icon: <Home className="h-6 w-6" />, title: 'Fencing & Gate Solutions', desc: 'Quality fencing that adds privacy, security, and defines your property boundaries' },
              { icon: <TreeDeciduous className="h-6 w-6" />, title: 'Tree Removal & Landscaping', desc: 'Safe tree removal and coordinated landscaping to protect your home and enhance beauty' },
              { icon: <Trash2 className="h-6 w-6" />, title: 'Junk & Debris Removal', desc: 'Efficient cleanup services to clear your property of construction debris and unwanted items' }
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-50 text-[hsl(var(--primary))] flex items-center justify-center">
                        {service.icon}
                      </div>
                      <div>
                        <CardTitle className="text-lg mb-2">{service.title}</CardTitle>
                        <CardDescription className="text-slate-600">{service.desc}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Approach Your Home Project */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              How We Approach Your Home Project
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Simple, transparent process so you always know what's happening and when.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-5 gap-6 max-w-6xl mx-auto">
            {[
              { step: '1', title: 'Schedule a Free Inspection', desc: 'We visit your home, assess the work needed, and answer all your questions' },
              { step: '2', title: 'Clear, Written Proposal', desc: 'Detailed quote with photo evidence showing exactly what needs to be done' },
              { step: '3', title: 'Project Scheduling', desc: 'We coordinate timing that works for your family and keep you updated' },
              { step: '4', title: 'Professional Execution', desc: 'Licensed, insured contractors complete your project with quality workmanship' },
              { step: '5', title: 'Final Walkthrough & Warranty', desc: 'We review the completed work with you and provide warranty documentation' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[hsl(var(--primary))] text-white text-2xl font-bold mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Homeowners Choose Elite Roofing */}
      <section className="py-16 md:py-24 bg-[hsl(var(--primary))] text-white">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Homeowners Choose Elite Roofing & Construction
            </h2>
            <p className="text-xl text-slate-200 max-w-3xl mx-auto">
              We're committed to earning your trust through every interaction and every project.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { icon: <Home className="h-8 w-8" />, title: 'Locally Trusted in Central Florida', desc: 'Serving Orlando, Kissimmee, Clermont, Winter Garden, Sanford, Lake Nona, and Davenport' },
              { icon: <CheckCircle2 className="h-8 w-8" />, title: 'Transparent Pricing & Documentation', desc: 'Clear quotes with detailed photo evidence — no surprises or hidden fees' },
              { icon: <Phone className="h-8 w-8" />, title: 'Responsive Communication', desc: 'Reach us by phone, email, or text. We keep you updated every step of the way' },
              { icon: <Shield className="h-8 w-8" />, title: 'Licensed & Insured Professionals', desc: 'All work performed by properly licensed and insured contractors' },
              { icon: <CheckCircle2 className="h-8 w-8" />, title: 'Project Follow-Through', desc: 'We manage every detail from start to finish so you do not have to worry' },
              { icon: <Shield className="h-8 w-8" />, title: 'Warranty Support', desc: 'Backed by manufacturer and workmanship warranties for your peace of mind' }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                <p className="text-slate-200">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              What Homeowners Are Saying
            </h2>
            <p className="text-xl text-slate-600">
              Real feedback from real Florida homeowners
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: 'Sarah M.', location: 'Winter Garden', text: 'Elite Roofing replaced our entire roof after storm damage. They handled everything with our insurance company and kept us informed every step of the way. The quality of work exceeded our expectations!', rating: 5 },
              { name: 'Michael R.', location: 'Orlando', text: 'We needed a quick roof repair before selling our home. They responded within 24 hours, fixed the leak, and provided documentation that satisfied our home inspector. Could not ask for better service.', rating: 5 },
              { name: 'Jennifer T.', location: 'Kissimmee', text: 'As a first-time homeowner, I was nervous about roof maintenance. The team explained everything clearly, showed me photos of problem areas, and completed repairs on schedule. Highly recommend!', rating: 5 }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full">
                  <CardContent className="p-6">
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-slate-700 mb-4 italic">"{testimonial.text}"</p>
                    <div className="border-t border-slate-200 pt-4">
                      <p className="font-semibold text-slate-900">{testimonial.name}</p>
                      <p className="text-sm text-slate-600">{testimonial.location}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Ready to Protect Your Home?
            </h2>
            <p className="text-xl text-slate-600">
              Request Your Free Residential Inspection & Quote Today
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="shadow-2xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-sm font-semibold text-slate-700 mb-2 block">
                        Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="h-12"
                        data-testid="residential-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-sm font-semibold text-slate-700 mb-2 block">
                        Phone <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="(407) 555-1234"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        className="h-12"
                        data-testid="residential-phone-input"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email" className="text-sm font-semibold text-slate-700 mb-2 block">
                        Email <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your.email@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="h-12"
                        data-testid="residential-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="service_needed" className="text-sm font-semibold text-slate-700 mb-2 block">
                        Select Service Needed
                      </Label>
                      <Select
                        value={formData.service_needed}
                        onValueChange={(value) => setFormData({ ...formData, service_needed: value })}
                      >
                        <SelectTrigger className="h-12" data-testid="residential-service-select">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="roof-inspection">Roof Inspection</SelectItem>
                          <SelectItem value="roof-repair">Roof Repair</SelectItem>
                          <SelectItem value="roof-replacement">Roof Replacement</SelectItem>
                          <SelectItem value="leak-repair">Leak Repair</SelectItem>
                          <SelectItem value="storm-damage">Storm Damage Assessment</SelectItem>
                          <SelectItem value="stucco-repair">Stucco Repair</SelectItem>
                          <SelectItem value="exterior-painting">Exterior Painting</SelectItem>
                          <SelectItem value="concrete-pavers">Concrete/Pavers</SelectItem>
                          <SelectItem value="fencing">Fencing</SelectItem>
                          <SelectItem value="tree-removal">Tree Removal</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <AddressAutocomplete
                    value={formData.address}
                    onChange={(text) => setFormData((s) => ({ ...s, address: text }))}
                    onPlaceSelected={(p) => setFormData((s) => ({
                      ...s,
                      address: p.formatted || p.street || s.address,
                      city: p.city || s.city,
                      state: p.state || s.state,
                      zip_code: p.zip_code || s.zip_code,
                    }))}
                    label="Property Address"
                    required={false}
                    testId="residential-address-input"
                    placeholder="Start typing the property address..."
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-2 block">City</Label>
                      <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="h-12" placeholder="City" data-testid="residential-city-input" />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-2 block">State</Label>
                      <Input value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })} maxLength={2} className="h-12" placeholder="FL" data-testid="residential-state-input" />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-2 block">Zip Code</Label>
                      <Input value={formData.zip_code} onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })} className="h-12" placeholder="ZIP" data-testid="residential-zip-input" />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-sm font-semibold text-slate-700 mb-2 block">
                      Tell Us About Your Project
                    </Label>
                    <Textarea
                      id="message"
                      placeholder="Please describe what you need help with..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={5}
                      className="resize-none"
                      data-testid="residential-message-textarea"
                    />
                  </div>

                  <ImageUploader
                    files={photos}
                    onChange={setPhotos}
                    maxFiles={5}
                    maxMb={10}
                    label="Attach photos (optional)"
                    hint="Photos of the damage, roof area, or scope. Up to 5 images, 10MB each."
                    testId="residential-photos"
                  />

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-14 bg-[hsl(var(--accent))] hover:bg-[#a01830] text-white text-lg font-bold shadow-xl"
                    data-testid="residential-submit-btn"
                  >
                    {isSubmitting ? 'Submitting...' : 'Get My Free Inspection & Quote'}
                  </Button>

                  <div className="text-center space-y-3 pt-4 border-t border-slate-200">
                    <p className="text-sm text-slate-600">Or reach out directly:</p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                      <a href="tel:+14077850642" className="flex items-center gap-2 text-[hsl(var(--primary))] hover:underline font-semibold">
                        <Phone className="h-4 w-4" />
                        (407) 785-0642
                      </a>
                      <a href="mailto:jeremy@elitercfl.com" className="flex items-center gap-2 text-[hsl(var(--primary))] hover:underline font-semibold">
                        <Mail className="h-4 w-4" />
                        jeremy@elitercfl.com
                      </a>
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      <Footer />
      <FloatingCTAs />
    </div>
  );
}
